import { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 22.7.14 (Tier 1 build, per docs/BUILD_ROADMAP.md): Operations dashboard.
// Single "fingertips" view of system + agent + cron health. Surfaces two existing
// endpoints with zero new backend work:
//   GET /api/health                          -> version, uptime, dependency probes
//                                               (includes kairos_prediction_cycle,
//                                               the freeze probe added in 22.7.13).
//   GET /api/admin/orchestration/dashboard   -> buildCouncilDashboard: commander
//                                               cadence/cost, resolution coverage,
//                                               cost burn, credibility, divergence,
//                                               trust gate, soft-watch checks.
// This is the page that would have surfaced the 22.7.11 KAIROS prediction-cycle
// outage at a glance (the kairos_prediction_cycle dependency card goes red).

// ─── colour helpers ────────────────────────────────────────────────────
const GREEN = '#10b981';
const AMBER = '#f59e0b';
const RED = '#ef4444';
const MUTED = '#6b7280';

function statusColor(s) {
  const v = String(s || '').toUpperCase();
  if (['PASS', 'OK', 'ACTIVE', 'GOOD', 'OPEN'].includes(v)) return GREEN;
  if (['WATCH', 'WARN', 'WAITING', 'PENDING', 'IDLE_OR_STALE', 'STALE'].includes(v)) return AMBER;
  if (['FAIL', 'OVER', 'NO_HISTORY', 'ERROR', 'DOWN', 'CLOSED'].includes(v)) return RED;
  return MUTED;
}

function Dot({ ok }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full shrink-0"
      style={{ backgroundColor: ok ? GREEN : RED }}
    />
  );
}

function Badge({ label, color }) {
  return (
    <span
      className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
      style={{ color: color || MUTED, backgroundColor: `${color || MUTED}1f` }}
    >
      {label}
    </span>
  );
}

function fmtAge(min) {
  if (min == null) return 'n/a';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtUsd(n) {
  if (n == null) return '-';
  return `$${(Math.round(n * 100) / 100).toLocaleString()}`;
}

// Compact one-line detail for a dependency probe.
function depDetail(name, d) {
  if (!d) return '';
  if (d.error) return d.error;
  if (name === 'kairos_prediction_cycle') {
    const base = `last #${d.last_prediction_id ?? '?'} - ${fmtAge(d.stale_minutes)} ago (thr ${d.threshold_minutes}m)`;
    // 22.7.87: the probe now alarms on consecutive ZERO-OUTPUT cycles, not
    // just cycle age. Show the streak whenever it is running so the card
    // explains itself before it goes red.
    const zero = d.consecutive_zero_cycles > 0
      ? ` - ${d.consecutive_zero_cycles} zero-output cycles in a row (alarm ${d.zero_streak_alarm_at ?? 12})`
      : '';
    const llm = d.last_llm_call;
    if (!llm) return base + zero;
    const llmStr = `llm ${llm.status || '?'}${llm.error ? `: ${llm.error}` : ''} (${fmtAge(llm.stale_minutes)} ago)`;
    return `${base}${zero} - ${llmStr}`;
  }
  if (d.stale_minutes != null) return `${fmtAge(d.stale_minutes)} stale (thr ${d.threshold_minutes ?? '?'}m)`;
  if (d.pct != null) return `${d.pct}% used (thr ${d.threshold_pct ?? '?'}%)`;
  const rest = Object.entries(d)
    .filter(([k]) => !['ok', 'critical'].includes(k))
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(' ');
  return rest;
}

// ─── data ──────────────────────────────────────────────────────────────
export default function OperationsPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('operations'); }, []);

  const [health, setHealth] = useState(null);
  const [dash, setDash] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async (bypassCache = false) => {
    setLoading(true);
    setErr(null);
    try {
      const [hRes, dRes] = await Promise.all([
        fetch('/api/health'),
        fetch(`/api/admin/orchestration/dashboard${bypassCache ? '?bypass_cache=true' : ''}`),
      ]);
      const h = hRes.ok ? await hRes.json() : { _error: `health HTTP ${hRes.status}` };
      const d = dRes.ok ? await dRes.json() : { _error: `dashboard HTTP ${dRes.status}` };
      setHealth(h);
      setDash(d);
      if (h._error && d._error) setErr(`${h._error}; ${d._error}`);
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const iv = setInterval(() => load(false), 60000);
    return () => clearInterval(iv);
  }, [load]);

  const deps = health?.dependencies ? Object.entries(health.dependencies) : [];
  const depsDown = deps.filter(([, d]) => d?.ok === false);
  const commanders = dash?.council?.commanders || [];
  const inactivePantheon = dash?.council?.inactive_pantheon || [];
  const resolution = dash?.resolution || null;
  const cost = dash?.cost || null;
  const credibility = dash?.credibility || null;
  const divergence = dash?.divergence || null;
  const trustGate = dash?.trust_gate || null;
  const softWatch = dash?.soft_watch_status || [];

  const overallOk = health?.ok !== false && depsDown.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Activity className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Operations</h1>
        <span className="text-korinth-muted text-sm">System, agent, and cron health at a glance</span>
        <button
          onClick={() => load(true)}
          className="ml-auto flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1.5 rounded border border-korinth-border/40 text-korinth-text hover:bg-korinth-border/40 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {err && <div className="text-xs text-red-400 font-mono">{err}</div>}

      {/* Top status strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border border-korinth-border/30 bg-korinth-bg/40">
          <div className="text-[10px] text-korinth-muted uppercase tracking-wide">Overall</div>
          <div className="flex items-center gap-2 mt-1">
            <Dot ok={overallOk} />
            <span className="text-sm font-bold" style={{ color: overallOk ? GREEN : RED }}>
              {overallOk ? 'HEALTHY' : `${depsDown.length} DEGRADED`}
            </span>
          </div>
        </div>
        <div className="p-3 rounded-lg border border-korinth-border/30 bg-korinth-bg/40">
          <div className="text-[10px] text-korinth-muted uppercase tracking-wide">Version / Sprint</div>
          <div className="text-sm font-mono text-white mt-1">v{health?.version ?? '?'} / {health?.sprint ?? '?'}</div>
        </div>
        <div className="p-3 rounded-lg border border-korinth-border/30 bg-korinth-bg/40">
          <div className="text-[10px] text-korinth-muted uppercase tracking-wide">Uptime</div>
          <div className="text-sm font-mono text-white mt-1">
            {health?.uptime != null ? `${Math.round((health.uptime / 3600) * 10) / 10}h` : 'n/a'}
          </div>
        </div>
        <div className="p-3 rounded-lg border border-korinth-border/30 bg-korinth-bg/40">
          <div className="text-[10px] text-korinth-muted uppercase tracking-wide">Cost (today / proj)</div>
          <div className="text-sm font-mono text-white mt-1">
            {cost ? `${fmtUsd(cost.today_usd)} / ${fmtUsd(cost.monthly_projection_usd)}` : 'n/a'}
            {cost?.vs_alarm_status && (
              <span className="ml-2"><Badge label={cost.vs_alarm_status} color={statusColor(cost.vs_alarm_status)} /></span>
            )}
          </div>
        </div>
      </div>

      {/* Dependencies */}
      <ModuleContainer title="Dependencies" subtitle="Cron + data-freshness probes (red = stale past threshold)">
        {deps.length === 0 ? (
          <div className="text-[11px] text-korinth-muted">{loading ? 'Loading...' : 'No dependency data.'}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {deps.map(([name, d]) => (
              <div
                key={name}
                className="flex items-start gap-2 p-2 rounded border border-korinth-border/20 bg-korinth-bg/30"
              >
                <span className="mt-1"><Dot ok={d?.ok !== false} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-mono text-white truncate">{name}</span>
                    {d?.critical && <Badge label="critical" color={AMBER} />}
                  </div>
                  <div className="text-[10px] font-mono text-korinth-muted break-words mt-0.5">{depDetail(name, d)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ModuleContainer>

      {/* Agents / commanders */}
      <ModuleContainer title="Council agents" subtitle="Cadence, last run, and today's cost per commander">
        {commanders.length === 0 ? (
          <div className="text-[11px] text-korinth-muted">{loading ? 'Loading...' : 'No commander data.'}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead>
                <tr className="text-korinth-muted border-b border-korinth-border/30">
                  <th className="text-left py-1.5 px-2">Agent</th>
                  <th className="text-left py-1.5 px-2">Domain</th>
                  <th className="text-left py-1.5 px-2">Cadence</th>
                  <th className="text-right py-1.5 px-2">Last run</th>
                  <th className="text-right py-1.5 px-2">Calls 24h</th>
                  <th className="text-right py-1.5 px-2">Cost today</th>
                  <th className="text-left py-1.5 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {commanders.map((c) => (
                  <tr key={c.agent} className="border-b border-korinth-border/10 hover:bg-korinth-bg/50">
                    <td className="py-1.5 px-2 text-white">{c.agent}</td>
                    <td className="py-1.5 px-2 text-korinth-muted">{c.domain}</td>
                    <td className="py-1.5 px-2 text-korinth-muted">{c.cadence}</td>
                    <td className="py-1.5 px-2 text-right text-korinth-text">{fmtAge(c.minutes_since_last)} ago</td>
                    <td className="py-1.5 px-2 text-right text-korinth-text">{c.sample_size_24h ?? 0}</td>
                    <td className="py-1.5 px-2 text-right text-korinth-text">{fmtUsd(c.cost_today_usd)}</td>
                    <td className="py-1.5 px-2">
                      <Badge label={c.status} color={statusColor(c.status)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {inactivePantheon.length > 0 && (
          <div className="mt-3 text-[10px] text-korinth-muted">
            Inactive by design (not standalone council commanders):{' '}
            {inactivePantheon.map((a) => a.agent || a.name || a).join(', ')}
          </div>
        )}
      </ModuleContainer>

      {/* Resolution + cost row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ModuleContainer title="Prediction resolution (24h)" subtitle="KAIROS win-condition coverage + auto-resolution">
          {!resolution || resolution._error ? (
            <div className="text-[11px] text-korinth-muted">{resolution?._error || (loading ? 'Loading...' : 'No data.')}</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
              <Stat label="Coverage" value={`${resolution.coverage_pct_24h}%`} sub={`target ${resolution.target_pct}%`} color={statusColor(resolution.coverage_pct_24h >= 90 ? 'PASS' : resolution.coverage_pct_24h >= 60 ? 'WATCH' : 'FAIL')} />
              <Stat label="Predictions 24h" value={resolution.predictions_total_24h} />
              <Stat label="Auto-resolved" value={resolution.auto_resolved_24h} />
              <Stat label="Still pending" value={resolution.still_pending} />
              <Stat label="With machine cond." value={resolution.with_machine_condition} />
              <Stat label="Uncategorized null" value={resolution.uncategorized_null} color={resolution.uncategorized_null > 0 ? AMBER : MUTED} />
            </div>
          )}
        </ModuleContainer>

        <ModuleContainer title="Cost burn" subtitle="Spend today, 7d, and monthly projection vs alarm">
          {!cost || cost._error ? (
            <div className="text-[11px] text-korinth-muted">{cost?._error || (loading ? 'Loading...' : 'No data.')}</div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
                <Stat label="Today" value={fmtUsd(cost.today_usd)} />
                <Stat label="Last 7d" value={fmtUsd(cost.last_7d_usd)} />
                <Stat label="Monthly proj." value={`${fmtUsd(cost.monthly_projection_usd)}/mo`} sub={`alarm ${fmtUsd(cost.alarm_threshold_monthly_usd)}`} color={statusColor(cost.vs_alarm_status)} />
                <Stat
                  label="Week-over-week"
                  value={cost.week_over_week?.growth_pct != null ? `${cost.week_over_week.growth_pct >= 0 ? '+' : ''}${cost.week_over_week.growth_pct}%` : (cost.week_over_week?.status || 'n/a')}
                  color={statusColor(cost.week_over_week?.status)}
                />
              </div>
              {cost.by_layer_today_usd && (
                <div className="text-[10px] font-mono text-korinth-muted">
                  By layer:{' '}
                  {Object.entries(cost.by_layer_today_usd)
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => `${k} ${fmtUsd(v)}`)
                    .join(' · ') || 'none today'}
                </div>
              )}
            </div>
          )}
        </ModuleContainer>
      </div>

      {/* Credibility + divergence + trust gate row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ModuleContainer title="Credibility (THEMIS)">
          {!credibility || credibility._error ? (
            <div className="text-[11px] text-korinth-muted">{credibility?._error || (loading ? 'Loading...' : 'No data.')}</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
              <Stat label="Sources tracked" value={credibility.total_sources_tracked} />
              <Stat label="Mean trust" value={credibility.mean_trust ?? 'n/a'} />
              <Stat label="With outcomes" value={credibility.sources_with_outcomes} />
              <Stat
                label="THEMIS age"
                value={fmtAge(credibility.themis_age_hours != null ? Math.round(credibility.themis_age_hours * 60) : null)}
                color={credibility.themis_stale ? RED : GREEN}
              />
            </div>
          )}
        </ModuleContainer>

        <ModuleContainer title="Divergence (NEMESIS)">
          {!divergence || divergence._error ? (
            <div className="text-[11px] text-korinth-muted">{divergence?._error || (loading ? 'Loading...' : 'No data.')}</div>
          ) : (
            <div className="space-y-2 text-[11px] font-mono">
              <div className="flex items-center justify-between">
                <span className="text-korinth-muted">Latest verdict</span>
                <Badge label={divergence.latest_run?.verdict || 'none'} color={statusColor(divergence.latest_run?.verdict === 'converged' ? 'FAIL' : 'PASS')} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-korinth-muted">Total runs</span>
                <span className="text-korinth-text">{divergence.total_runs}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-korinth-muted">Groupthink alarms 7d</span>
                <span style={{ color: divergence.groupthink_alarm_count_7d > 0 ? AMBER : MUTED }}>{divergence.groupthink_alarm_count_7d}</span>
              </div>
            </div>
          )}
        </ModuleContainer>

        <ModuleContainer title="Trust gate">
          {!trustGate || trustGate._error ? (
            <div className="text-[11px] text-korinth-muted">{trustGate?._error || (loading ? 'Loading...' : 'No data.')}</div>
          ) : (
            <div className="space-y-1.5 text-[11px] font-mono">
              {Object.entries(trustGate)
                .filter(([k]) => !k.startsWith('_'))
                .slice(0, 8)
                .map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-2">
                    <span className="text-korinth-muted truncate">{k}</span>
                    <span className="text-korinth-text text-right truncate">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                  </div>
                ))}
            </div>
          )}
        </ModuleContainer>
      </div>

      {/* Soft watch */}
      <ModuleContainer title="Soft-watch checks" subtitle="Synthetic health checks against current dashboard data">
        {softWatch.length === 0 ? (
          <div className="text-[11px] text-korinth-muted">{loading ? 'Loading...' : 'No checks.'}</div>
        ) : (
          <div className="space-y-1.5">
            {softWatch.map((it, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded border border-korinth-border/20 bg-korinth-bg/30">
                <Badge label={it.status} color={statusColor(it.status)} />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-white truncate">{it.item}</div>
                  <div className="text-[10px] font-mono text-korinth-muted">now: {it.current} · target: {it.target}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ModuleContainer>

      {refreshedAt && (
        <div className="text-[10px] text-korinth-muted font-mono">
          Refreshed {refreshedAt.toLocaleTimeString()} · auto-refresh 60s
          {dash?._cache ? ` · dashboard cache ${dash._cache.hit ? 'hit' : 'fresh'}` : ''}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div>
      <div className="text-korinth-muted text-[10px]">{label}</div>
      <div className="text-base font-bold" style={{ color: color || '#e5e7eb' }}>{value}</div>
      {sub && <div className="text-[9px] text-korinth-muted">{sub}</div>}
    </div>
  );
}
